// TanStack Query hooks for /api/masters/*.
// Each master has list / get / create / update / softDelete hooks. Mutations invalidate
// the relevant list queries automatically.

import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type ListResponse<T> = {
  rows: T[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
};

export type ListQuery = {
  page?: number;
  pageSize?: number;
  q?: string;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
};

function makeMasterHooks<T, CreateInput = Partial<T>, UpdateInput = Partial<T>>(base: string, key: string) {
  function listKey(q: ListQuery) {
    return [key, 'list', q];
  }
  function itemKey(id: string) {
    return [key, 'item', id];
  }

  function useList(q: ListQuery = {}, options?: Partial<UseQueryOptions<ListResponse<T>>>) {
    return useQuery<ListResponse<T>>({
      queryKey: listKey(q),
      queryFn: async () => {
        const res = await api.get(base, { params: q });
        return res.data;
      },
      placeholderData: (prev) => prev,
      ...options,
    });
  }

  function useGet(id: string | null) {
    return useQuery<T>({
      queryKey: itemKey(id ?? ''),
      queryFn: async () => {
        const res = await api.get(`${base}/${id}`);
        return res.data;
      },
      enabled: Boolean(id),
    });
  }

  // Invalidate the list AFTER cancelling any in-flight list fetch. Without the
  // cancel, a mount fetch that started BEFORE the mutation can resolve after it
  // and be reused as the "fresh" result — the just-created row never appears
  // until a manual reload.
  async function refreshList(qc: ReturnType<typeof useQueryClient>) {
    await qc.cancelQueries({ queryKey: [key, 'list'] });
    await qc.invalidateQueries({ queryKey: [key, 'list'] });
  }

  function useCreate() {
    const qc = useQueryClient();
    return useMutation<T, unknown, CreateInput>({
      mutationFn: async (input) => {
        const res = await api.post(base, input);
        return res.data;
      },
      onSuccess: () => refreshList(qc),
    });
  }

  function useUpdate() {
    const qc = useQueryClient();
    return useMutation<T, unknown, { id: string; input: UpdateInput }>({
      mutationFn: async ({ id, input }) => {
        const res = await api.patch(`${base}/${id}`, input);
        return res.data;
      },
      onSuccess: (row) => {
        void refreshList(qc);
        qc.setQueryData(itemKey((row as { id: string }).id), row);
      },
    });
  }

  function useSoftDelete() {
    const qc = useQueryClient();
    return useMutation<T, unknown, { id: string; reason?: string }>({
      mutationFn: async ({ id, reason }) => {
        const res = await api.delete(`${base}/${id}`, { data: { reason } });
        return res.data;
      },
      onSuccess: () => refreshList(qc),
    });
  }

  function useRestore() {
    const qc = useQueryClient();
    return useMutation<T, unknown, { id: string }>({
      mutationFn: async ({ id }) => {
        const res = await api.post(`${base}/${id}/restore`);
        return res.data;
      },
      onSuccess: () => refreshList(qc),
    });
  }

  return { useList, useGet, useCreate, useUpdate, useSoftDelete, useRestore, base, key };
}

// ─── Typed shapes (loose; refined per page where needed) ──────────

export interface PartyRow {
  id: string;
  displayName: string;
  legalName: string;
  partyType: 'CUSTOMER' | 'VENDOR' | 'BOTH' | 'DISTRIBUTOR' | 'AGENT' | 'EMPLOYEE';
  gstin?: string | null;
  pan?: string | null;
  tan?: string | null;
  msmeClassification: 'NOT_MSME' | 'MICRO' | 'SMALL' | 'MEDIUM';
  msmeUdyam?: string | null;
  tdsSection?: string | null;
  tdsRatePercent?: string | number | null;
  tdsThresholdAnnual?: string | number | null;
  creditLimit: string | number;
  creditDays: number;
  isActive: boolean;
  deletedAt?: string | null;
  ledger?: { id: string; name: string; billByBill?: boolean; group?: { id: string; name: string } | null };
  ledgerId: string;
  companyId: string;
  openingBalance?: string | number;
  openingBalanceMode?: 'DR' | 'CR' | 'NIL';
  notes?: string | null;
  priceListId?: string | null;
  salesmanName?: string | null;
  area?: string | null;
}

export interface LedgerRow {
  id: string;
  name: string;
  groupId: string;
  group?: { id: string; name: string; nature: string; behavesLike: string };
  openingBalance: string | number;
  isDebitBalance: boolean;
  billByBill: boolean;
  isControl: boolean;
  gstApplicable: string;
  gstin?: string | null;
  pan?: string | null;
  companyId: string;
  deletedAt?: string | null;
}

export interface ItemRow {
  id: string;
  name: string;
  itemType: 'STOCK' | 'SERVICE' | 'FIXED_ASSET';
  hsnSacCode?: string | null;
  gstRatePercent: string | number;
  unitId: string;
  unit?: { id: string; code: string; name: string };
  stockGroupId?: string | null;
  stockGroup?: { id: string; name: string } | null;
  salePrice?: string | number | null;
  purchasePrice?: string | number | null;
  mrp?: string | number | null;
  reorderLevel: string | number;
  isActive: boolean;
  deletedAt?: string | null;
  companyId: string;
}

export interface ChartGroupRow {
  id: string;
  name: string;
  parentId?: string | null;
  nature: 'ASSET' | 'LIABILITY' | 'INCOME' | 'EXPENSE';
  behavesLike: string;
  isReserved: boolean;
  sortOrder: number;
  parent?: { id: string; name: string };
}

export interface UnitRow {
  id: string;
  code: string;
  name: string;
  decimalPlaces: number;
  isCompound: boolean;
  primaryUnitId?: string | null;
  conversionFactor: string | number;
  isActive: boolean;
}

export type VoucherBaseTypeCode =
  | 'SALES' | 'PURCHASE' | 'RECEIPT' | 'PAYMENT' | 'CONTRA' | 'JOURNAL'
  | 'CREDIT_NOTE' | 'DEBIT_NOTE' | 'DELIVERY_NOTE' | 'RECEIPT_NOTE'
  | 'SALES_ORDER' | 'PURCHASE_ORDER' | 'QUOTATION' | 'PROFORMA_INVOICE'
  | 'STOCK_JOURNAL' | 'PHYSICAL_STOCK' | 'MATERIAL_IN' | 'MATERIAL_OUT'
  | 'MANUFACTURING_JOURNAL' | 'REJECTION_IN' | 'REJECTION_OUT'
  | 'MEMORANDUM' | 'REVERSING_JOURNAL' | 'ATTENDANCE' | 'PAYROLL'
  | 'JOB_WORK_IN_ORDER' | 'JOB_WORK_OUT_ORDER' | 'INTER_BRANCH_TRANSFER';

export interface VoucherTypeRow {
  id: string;
  code: VoucherBaseTypeCode;
  baseTypeCode?: VoucherBaseTypeCode | null;
  name: string;
  abbreviation: string;
  numberingPrefix: string;
  numberingPattern: string;
  startNumber: number;
  currentNumber: number;
  restartOnFyChange: boolean;
  lastNumberFyLabel?: string | null;
  isActive: boolean;
  isReserved: boolean;
  isUserDefined: boolean;
  printTemplateId?: string | null;
  behaviourFlags?: Record<string, unknown>;
  notes?: string | null;
  branchId?: string | null;
  companyId: string;
  deletedAt?: string | null;
}

// HSN/SAC ─────────────────────────────────────────────────────────
export interface HsnSacRow {
  id: string;
  code: string;
  type: 'HSN' | 'SAC';
  description: string;
  defaultGstRatePercent: number | string;
  defaultGstCessPercent: number | string;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Stock Group ─────────────────────────────────────────────────────
export interface StockGroupRow {
  id: string;
  name: string;
  parentId: string | null;
  parent?: { id: string; name: string } | null;
  sortOrder: number;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

// Cost Category ────────────────────────────────────────────────────
export interface CostCategoryRow {
  id: string;
  name: string;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

// Cost Centre ──────────────────────────────────────────────────────
export interface CostCentreRow {
  id: string;
  name: string;
  categoryId: string | null;
  category?: { id: string; name: string } | null;
  parentId: string | null;
  parent?: { id: string; name: string } | null;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

// Tax Rate ─────────────────────────────────────────────────────────
export interface TaxRateRow {
  id: string;
  taxType: 'GST' | 'CESS' | 'TDS' | 'TCS';
  ratePercent: number | string;
  cgstPercent: number | string;
  sgstPercent: number | string;
  igstPercent: number | string;
  section?: string | null;
  description?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Price Level ──────────────────────────────────────────────────────
export interface PriceLevelRow {
  id: string;
  name: string;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface ItemPriceRow {
  id?: string;
  itemId: string;
  itemName?: string;
  standardRate?: string | number | null;
  minQty: string | number;
  rate: string | number;
  discountPercent: string | number;
}

// ─── Hooks ────────────────────────────────────────────────────────

export const hsnSac = makeMasterHooks<HsnSacRow>('/masters/hsn-sac', 'hsnSac');
export const stockGroups = makeMasterHooks<StockGroupRow>('/masters/stock-groups', 'stockGroups');
export const costCategories = makeMasterHooks<CostCategoryRow>('/masters/cost-categories', 'costCategories');
export const costCentres = makeMasterHooks<CostCentreRow>('/masters/cost-centres', 'costCentres');
export const taxRates = makeMasterHooks<TaxRateRow>('/masters/tax-rates', 'taxRates');
export const parties = makeMasterHooks<PartyRow>('/masters/parties', 'parties');
export const ledgers = makeMasterHooks<LedgerRow>('/masters/ledgers', 'ledgers');
export const items = makeMasterHooks<ItemRow>('/masters/items', 'items');
export const chartGroups = makeMasterHooks<ChartGroupRow>('/masters/chart-groups', 'chartGroups');
export const units = makeMasterHooks<UnitRow>('/masters/units', 'units');
export const voucherTypes = makeMasterHooks<VoucherTypeRow>('/masters/voucher-types', 'voucherTypes');
export const priceLevels = makeMasterHooks<PriceLevelRow>('/masters/price-levels', 'priceLevels');

// Price-level rate resolution — voucher entry prefill. Returns null rate when the
// party has no price level (caller falls back to item.salePrice).
export async function resolvePartyItemRate(partyId: string, itemId: string, qty: number) {
  const res = await api.get<{ rate: string | number | null; discountPercent: string | number | null }>(
    '/masters/price-resolve',
    { params: { partyId, itemId, qty } },
  );
  return res.data;
}
